/* eslint-disable no-console */
import { post } from '@chat/utils';
import { FAQ_STOP_FLOW, getFAQWorkflowName } from '@/api/url';
import { workflowReplyPayloadCases } from './WorkflowSSEUtil.cases';

const appendWorkflowAuth = (url: string, authQuery: string): string => {
    const separator = url.indexOf('?') >= 0 ? '&' : '?';
    const query = authQuery.charAt(0) === '?' ? authQuery.slice(1) : authQuery;
    return `${url}${separator}${query}`;
};

/** 后端接受的请求体结构，按 API 文档扩展 */
export interface WorkflowRequestBody {
    inputs: Record<string, any>;
    response_mode?: 'streaming' | 'blocking';
    user?: string;
    [key: string]: any;
}

/** 会话及任务 id 在顶层，流式文本在 event=message 的 answer 字段，完成时在 data.outputs.answer */
interface WorkflowSSEEnvelope {
    event: string;
    conversation_id?: string;
    workflow_run_id?: string;
    task_id?: string;
    answer?: string;
    data?: any;
}

export interface WorkflowSSERequestOptions {
    url: string;
    authQuery: string;
    body: WorkflowRequestBody;
    timeout?: number;
    debug?: boolean;
}

/** 工作流 SSE 请求的返回结果 */
export interface WorkflowSSEResult {
    fullText: string;
    conversation_id: string;
    workflow_run_id: string;
    task_id: string;
}

export interface WorkflowReplyPayload {
    need_human?: boolean;
    reply?: string;
    reason_type?: string;
    sop_hit?: boolean;
}

const WORKFLOW_REPLY_PAYLOAD_CASE_KEY = 'im-workflow-reply-case';

/** 从文本中倒序提取最后一个完整 JSON 对象 */
function extractLastJsonObject(text: string): string {
    let depth = 0;
    let endIndex = -1;
    let startIndex = -1;
    let inString = false;
    let isEscaped = false;

    for (let index = text.length - 1; index >= 0; index -= 1) {
        const char = text[index];

        if (inString) {
            if (isEscaped) {
                isEscaped = false;
            } else if (char === '\\') {
                isEscaped = true;
            } else if (char === '"') {
                inString = false;
            }
        } else if (char === '"') {
            inString = true;
        } else if (char === '}') {
            if (endIndex === -1) {
                endIndex = index;
            }
            depth += 1;
        } else if (char === '{' && depth > 0) {
            depth -= 1;
            if (depth === 0) {
                startIndex = index;
                break;
            }
        }
    }

    return startIndex !== -1 && endIndex !== -1 ? text.slice(startIndex, endIndex + 1) : '';
}

function getWorkflowReplyPayloadMockFullText(fullText: string): string {
    if (typeof localStorage === 'undefined') {
        return fullText;
    }

    const caseSelector = localStorage.getItem(WORKFLOW_REPLY_PAYLOAD_CASE_KEY)?.trim();
    if (!caseSelector) {
        return fullText;
    }

    // 调试时可通过 localStorage 指定 case，下标或名称命中后会直接覆盖真实返回内容。
    const matchedCase =
        workflowReplyPayloadCases.find((item, index) => `${index}` === caseSelector) ||
        workflowReplyPayloadCases.find((item) => item.name === caseSelector);

    return matchedCase?.fullText || fullText;
}

export function parseWorkflowReplyPayload(fullText: string): WorkflowReplyPayload {
    // const mockText = getWorkflowReplyPayloadMockFullText(fullText);
    const jsonText = extractLastJsonObject(fullText.trim());
    return JSON.parse(jsonText) as WorkflowReplyPayload;
}

/** 当前进行中的 SSE 请求对应的 AbortController，用于 abortWorkflowSSERequest() 终止 */
let currentController: AbortController | null = null;

/** 当前进行中的工作流 taskId，用于下次 abort 时携带 */
let currentWorkflowTaskId: string | null = null;

/** 上次 abort 的原因，用于区分超时和用户取消 */
let lastAbortReason: 'timeout' | null = null;

/** 是否因新请求而取消（新请求会自带 loading，被取消的请求不应移除） */
let abortedForNewRequest = false;

/** 不兼容错误标识，供调用方识别后走兜底策略 */
export const WORKFLOW_INCOMPATIBLE = 'WorkflowSSEIncompatible';

/** 空结果错误标识，供调用方识别后走兜底 FAQ 流程 */
export const WORKFLOW_EMPTY_RESULT = 'WorkflowSSEEmptyResult';

/** 超时错误标识 */
export const WORKFLOW_TIMEOUT = 'WorkflowSSETimeout';

/** 用户取消错误标识 */
export const WORKFLOW_ABORT = 'WorkflowSSEAbort';

type WorkflowErrorCode =
    | typeof WORKFLOW_INCOMPATIBLE
    | typeof WORKFLOW_EMPTY_RESULT
    | typeof WORKFLOW_TIMEOUT
    | typeof WORKFLOW_ABORT;

function createWorkflowError(
    message: string,
    code: WorkflowErrorCode
): Error & Record<string, boolean> {
    const err = new Error(message) as Error & Record<string, boolean>;
    err[code] = true;
    return err;
}

function checkWorkflowSSECompat(): void {
    const missing: string[] = [];
    if (typeof fetch !== 'function') missing.push('fetch');
    if (typeof AbortController === 'undefined') missing.push('AbortController');
    if (typeof TextDecoder === 'undefined') missing.push('TextDecoder');
    if (typeof ReadableStream === 'undefined') missing.push('ReadableStream');
    if (missing.length > 0) {
        throw createWorkflowError(
            `[WorkflowSSE] 当前环境不支持: ${missing.join(', ')}`,
            WORKFLOW_INCOMPATIBLE
        );
    }
}

function cleanupTimeout(timeoutId: ReturnType<typeof setTimeout> | null) {
    if (timeoutId !== null) {
        clearTimeout(timeoutId);
    }
}

/**
 * 终止当前正在进行的工作流 SSE 请求（若有）。
 * 可在任意时刻调用，用于用户主动取消。
 */
export async function abortWorkflowSSERequest(): Promise<void> {
    const controller = currentController;
    const taskId = currentWorkflowTaskId;

    // 先中断当前 SSE 连接
    if (controller) {
        console.debug('[WorkflowSSE] abort controller');
        controller.abort();
        currentController = null;
    }

    // 调用前就清空，避免重复 stop，并防止旧请求清理掉新请求的状态
    currentWorkflowTaskId = null;

    // 如果已拿到 workflow taskId，则调用客服后端停止接口；鉴权由后端统一处理
    if (taskId) {
        try {
            await post(FAQ_STOP_FLOW, {
                flow: getFAQWorkflowName(),
                task_id: taskId
            });
        } catch (err: any) {
            // 静默处理错误，避免影响取消流程
            console.debug('[WorkflowSSE] stop workflow error:', err);
        }
    }
}

/**
 * 发起一次工作流流式请求，返回完整回答文本、workflow_run_id 和 task_id
 */
export async function workflowSSERequest(
    options: WorkflowSSERequestOptions
): Promise<WorkflowSSEResult> {
    const { url, authQuery, body: requestBody, timeout = 60000, debug = false } = options;

    checkWorkflowSSECompat();

    if (!authQuery) {
        throw new Error('[WorkflowSSE] faq-faq 未返回工作流鉴权参数');
    }

    // 1. 每次新请求前，若有进行中的请求（含尚未拿到 task_id 的 pending 状态），则先取消
    if (currentController || currentWorkflowTaskId) {
        abortedForNewRequest = true;
        await abortWorkflowSSERequest();
    }

    const controller = new AbortController();
    currentController = controller;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (timeout > 0) {
        timeoutId = setTimeout(() => {
            if (debug) console.debug('[WorkflowSSE] timeout');
            lastAbortReason = 'timeout';
            // 超时同样通过 abortWorkflowSSERequest 走统一取消逻辑
            // 注意这里不 await，以免阻塞定时器；内部错误也静默处理
            abortWorkflowSSERequest().catch(() => {});
        }, timeout);
    }

    try {
        const body = JSON.stringify(requestBody);
        const resp = await fetch(appendWorkflowAuth(url, authQuery), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream'
            },
            body,
            signal: controller.signal
        });

        if (!resp.ok || !resp.body) {
            throw new Error(`SSE request failed: ${resp.status} ${resp.statusText}`);
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder('utf-8');

        let buffer = '';
        let fullText = '';
        let conversation_id = '';
        let workflow_run_id = '';
        let task_id = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const idx = buffer.indexOf('\n\n');
                if (idx === -1) break;

                const rawEvent = buffer.slice(0, idx).trim();
                buffer = buffer.slice(idx + 2);

                const dataLine = rawEvent.split('\n').find((line) => line.startsWith('data:'));

                if (dataLine) {
                    const jsonStr = dataLine.slice('data:'.length).trim();

                    let payload: WorkflowSSEEnvelope | null = null;
                    try {
                        payload = JSON.parse(jsonStr);
                    } catch (e) {
                        if (debug) console.debug('[WorkflowSSE] JSON parse error', e, jsonStr);
                    }

                    if (payload) {
                        const {
                            event,
                            conversation_id: conversationId,
                            workflow_run_id: runId,
                            task_id: taskId,
                            data
                        } = payload;
                        const eventRunId = runId || data?.workflow_run_id;
                        const eventTaskId = taskId || data?.task_id;

                        // 工作流一旦返回 task_id 就视为已建立，后续中断必须调用 stop。
                        if (!currentWorkflowTaskId && eventTaskId) {
                            currentWorkflowTaskId = eventTaskId;
                        }

                        // 收集 workflow_run_id 和 task_id（从第一个事件开始）
                        if (eventRunId && !workflow_run_id) {
                            workflow_run_id = eventRunId;
                        }
                        if (eventTaskId && !task_id) {
                            task_id = eventTaskId;
                        }
                        if (conversationId && !conversation_id) {
                            conversation_id = conversationId;
                        }

                        if (debug) console.debug('[WorkflowSSE] event:', event, payload);

                        if (event === 'message' && typeof payload.answer === 'string') {
                            fullText += payload.answer;
                        } else if (event === 'workflow_finished') {
                            const outputsAnswer = data?.outputs?.answer;
                            const finalText =
                                typeof outputsAnswer === 'string' && outputsAnswer.length > 0
                                    ? outputsAnswer
                                    : fullText;

                            cleanupTimeout(timeoutId);
                            if (currentController === controller) {
                                currentController = null;
                                currentWorkflowTaskId = null;
                            }

                            if (!finalText.trim()) {
                                throw createWorkflowError(
                                    '[WorkflowSSE] 工作流返回结果为空',
                                    WORKFLOW_EMPTY_RESULT
                                );
                            }

                            return {
                                fullText: finalText,
                                conversation_id: conversation_id || conversationId || '',
                                workflow_run_id: workflow_run_id || runId || '',
                                task_id: task_id || taskId || ''
                            };
                        }
                    }
                }
            }
        }

        if (!fullText.trim()) {
            throw createWorkflowError('[WorkflowSSE] 工作流返回结果为空', WORKFLOW_EMPTY_RESULT);
        }

        return {
            fullText,
            conversation_id,
            workflow_run_id: workflow_run_id || '',
            task_id: task_id || ''
        };
    } catch (e: any) {
        if (e?.name === 'AbortError') {
            const reason = lastAbortReason;
            lastAbortReason = null;
            if (reason === 'timeout') {
                throw createWorkflowError('[WorkflowSSE] 请求超时', WORKFLOW_TIMEOUT);
            }
            const err = createWorkflowError('[WorkflowSSE] 用户取消', WORKFLOW_ABORT) as Error & {
                abortedForNewRequest?: boolean;
            };
            err.abortedForNewRequest = abortedForNewRequest;
            abortedForNewRequest = false;
            throw err;
        }
        throw e;
    } finally {
        lastAbortReason = null;
        abortedForNewRequest = false;
        cleanupTimeout(timeoutId);
        if (currentController === controller) {
            currentController = null;
            currentWorkflowTaskId = null;
        }
    }
}
